import {
  Button,
  Divider,
  Group,
  Menu,
  Paper,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import type { ReactNode } from 'react';

/** One card of the document page's right-hand column. */
export function DocumentCard({
  title,
  icon,
  extra,
  children,
}: {
  title: string;
  icon: ReactNode;
  extra?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Paper withBorder p="sm">
      <Group gap="xs" mb="xs" justify="space-between" wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          {icon}
          <Text size="sm" fw={500}>
            {title}
          </Text>
        </Group>
        {extra}
      </Group>
      <Divider mb="xs" />
      {children}
    </Paper>
  );
}

export function FactRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <Group gap="xs" align="flex-start" wrap="nowrap">
      <Text size="xs" c="dimmed" w={84} style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Text size="xs" component="div" style={{ flex: 1, minWidth: 0 }}>
        {children}
      </Text>
    </Group>
  );
}

export const money = (
  amount: number | string | null | undefined,
  currency: string | null | undefined,
) =>
  `${Number(amount ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency ?? ''}`.trim();

/**
 * One item of an action menu. A disabled item prints why under its label (a disabled menu item
 * takes no hover, so a tooltip would never show); an enabled one may print what else it changes.
 */
export function ReasonItem({
  label,
  reason,
  hint,
  icon,
  color,
  onClick,
}: {
  label: string;
  reason: string | null;
  hint?: string;
  icon?: ReactNode;
  color?: string;
  onClick?: () => void;
}) {
  const note = reason ?? hint;
  return (
    <Menu.Item
      leftSection={icon}
      color={color}
      disabled={reason != null}
      onClick={onClick}
    >
      <Stack gap={0}>
        <Text fz="sm" inherit>
          {label}
        </Text>
        {note && (
          <Text fz={11} c="dimmed" lh={1.3}>
            {note}
          </Text>
        )}
      </Stack>
    </Menu.Item>
  );
}

/** A button of an action bar, and when it is disabled, why (on hover). */
export function ActionButton({
  label,
  reason,
  onClick,
  loading,
  icon,
  color,
  variant = 'default',
  href,
  after,
  hint,
}: {
  label: string;
  reason: string | null;
  onClick?: () => void;
  loading?: boolean;
  icon?: ReactNode;
  color?: string;
  variant?: 'default' | 'filled' | 'light';
  href?: string;
  after?: ReactNode;
  /** A tooltip of the enabled button; none when null. */
  hint?: string | null;
}) {
  const disabled = reason != null;
  const button =
    href && !disabled ? (
      <Button
        size="xs"
        variant={variant}
        color={color}
        leftSection={icon}
        component="a"
        href={href}
        target="_blank"
        rel="noreferrer"
      >
        {label}
      </Button>
    ) : (
      <Button
        size="xs"
        variant={variant}
        color={color}
        leftSection={icon}
        loading={loading}
        disabled={disabled}
        onClick={onClick}
        aria-description={reason ?? undefined}
      >
        {label}
      </Button>
    );
  return (
    <Group gap={4} wrap="nowrap" align="center">
      {disabled ? (
        <Tooltip label={reason} multiline w={260} withArrow fz="xs">
          <span style={{ display: 'inline-block', cursor: 'not-allowed' }}>
            {button}
          </span>
        </Tooltip>
      ) : hint ? (
        <Tooltip label={hint} withArrow fz="xs">
          {button}
        </Tooltip>
      ) : (
        button
      )}
      {after}
    </Group>
  );
}
